package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.alignItems
import uz.mint.frontend.api.Destination
import uz.mint.frontend.api.DestinationState
import uz.mint.frontend.api.pathIn
import uz.mint.frontend.api.rememberDestinations
import uz.mint.frontend.components.widgets.ArrowRightIcon
import uz.mint.frontend.components.widgets.DestinationGrid
import uz.mint.frontend.components.widgets.PlanNotice
import uz.mint.frontend.components.widgets.SearchBox
import uz.mint.frontend.components.widgets.SecondaryButton
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.justifyContent
import uz.mint.frontend.toSitePalette

/**
 * The landing page's destination section: **where are you going**, not what kind of bundle is this.
 *
 * This replaced three tabs — локальные / региональные / глобальные — that asked a customer to
 * classify their own trip against the supplier's taxonomy before they could see a price. Someone
 * flying to Türkiye knows the country; whether the plan is filed as "local" is our problem, not
 * theirs. Picking a destination is now the first step and choosing a package the second.
 *
 * The short list is curated server-side ([Destination.popular], from `mint.catalog.popular-countries`)
 * rather than computed: there are no sales figures to rank by, and ranking by plan count would put
 * Macao above Türkiye. Everything else is one link away on the full catalogue.
 *
 * Cards are links, so this section is also sixteen crawlable routes into a filtered catalogue.
 */
@Composable
fun Tariffs(locale: Locale, strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()
    val (state, retry) = rememberDestinations(locale)
    var query by remember { mutableStateOf("") }

    Div(
        ShellStyle.toModifier()
            .id(TARIFFS_ANCHOR)
            // Vertical padding here MUST repeat the horizontal gutter: Kobweb emits this as the
            // `padding` shorthand in an inline style, which beats ShellStyle's class and would
            // otherwise silently reset the side gutter to zero on a phone.
            .padding(top = clamp(48.px, 5.vw, 84.px), bottom = clamp(52.px, 5.5.vw, 92.px), leftRight = PageGutter)
            .toAttrs()
    ) {
        Row(
            Modifier.fillMaxWidth().gap(24.px).flexWrapCss()
                .margin(bottom = clamp(24.px, 2.6.vw, 38.px)),
            verticalAlignment = Alignment.Bottom,
        ) {
            Column(Modifier.gap(12.px)) {
                H2(
                    Modifier.fontSize(clamp(32.px, 3.6.vw, 52.px)).lineHeight(1)
                        .letterSpacing((-0.035).em).fontWeight(FontWeight.ExtraBold).toAttrs()
                ) { SpanText(strings.tariffsTitle) }
                P(Modifier.fontSize(16.px).color(palette.ink2).maxWidth(38.em).toAttrs()) {
                    SpanText(strings.tariffsSub)
                }
            }

            Spacer()

            // The search sits exactly where the локальные/региональные/глобальные tabs used to be,
            // and that placement is the point: it is the spot a customer's eye already goes when
            // they start choosing a tariff, and it is on the landing page, so finding a country
            // costs no navigation. It replaced the tabs rather than joining them because it does
            // the same job better — narrowing 191 destinations by name beats sorting them into
            // three buckets the traveller did not ask about.
            SearchBox(
                placeholder = strings.destSearchPlaceholder,
                query = query,
                onQuery = { query = it },
            )
        }

        when (state) {
            is DestinationState.Loading -> PlanNotice(strings.destLoading)

            is DestinationState.Failed -> DestinationError(strings, retry)

            is DestinationState.Loaded -> {
                val searching = query.isNotBlank()
                val matches = state.destinations.search(query)

                // Empty box: a curated shortlist. Typing: the real answer, searched across all 191
                // countries rather than the sixteen on screen — otherwise the box would appear to
                // know nothing about most of the catalogue.
                val countries = if (searching) matches.countries else state.destinations.popular

                // A preview, not the list: there are ~35 bundles and putting all of them on the
                // landing page doubles its length below the fold. Ranked by how many packages each
                // carries, which is the only signal in the data — there are no sales figures, and
                // alphabetical would lead with "Africa" and "AUKUS(3 countries)". While searching,
                // every match shows: a hidden match is a bug, not a tidy preview.
                val regions = if (searching) {
                    matches.regions
                } else {
                    state.destinations.regions.sortedByDescending { it.planCount }.take(LANDING_REGIONS)
                }

                if (countries.isEmpty() && regions.isEmpty()) {
                    PlanNotice(if (searching) strings.destNoMatch else strings.tariffsEmpty)
                } else {
                    if (countries.isNotEmpty()) {
                        BlockHeading(if (searching) strings.destAllCountries else strings.destPopular)
                        DestinationGrid(countries, locale, strings, href = { it.pathIn(locale) })
                    }

                    if (regions.isNotEmpty()) {
                        BlockHeading(strings.destRegions, topGap = countries.isNotEmpty())
                        DestinationGrid(regions, locale, strings, href = { it.pathIn(locale) })
                    }

                    AllDestinationsLink(locale, strings)
                }
            }
        }

        Row(
            Modifier.fillMaxWidth().gap(16.px).flexWrapCss().margin(top = 22.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(strings.tariffsNote, Modifier.fontSize(13.px).color(palette.ink2).opacity(0.8))
            Spacer()
            Link(
                path = locale.plansPath,
                modifier = Modifier.fontSize(15.px).fontWeight(FontWeight.Bold)
                    .borderBottom(2.px, LineStyle.Solid, palette.mint).padding(bottom = 2.px),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) { SpanText(strings.tariffsAll) }
        }
    }
}

/** How many multi-country bundles the landing page previews. See the note at the call site. */
private const val LANDING_REGIONS = 8

/** Small-caps label over each block of the picker. */
@Composable
private fun BlockHeading(text: String, topGap: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    SpanText(
        text,
        EyebrowStyle.toModifier()
            .display(DisplayStyle.Block)
            .fontSize(12.px).letterSpacing(0.1.em).color(palette.accentText)
            .margin(top = if (topGap) 34.px else 0.px, bottom = 12.px),
    )
}

/**
 * "Показать все страны →" — the way through to the other ~175 countries.
 *
 * The landing page shows a curated sixteen; the catalogue has 191 countries and 35 bundles behind
 * it, and this is the door.
 */
@Composable
private fun AllDestinationsLink(locale: Locale, strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()

    Link(
        path = locale.plansPath,
        modifier = Modifier
            .fillMaxWidth()
            .margin(top = 24.px)
            .backgroundColor(palette.solidBg).color(palette.solidFg)
            .display(DisplayStyle.Flex)
            .justifyContent("center")
            .alignItems("center")
            .gap(10.px)
            .padding(topBottom = 18.px, leftRight = 24.px)
            .fontSize(16.px).fontWeight(FontWeight.Bold),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) {
        SpanText(strings.destSeeAll)
        ArrowRightIcon(20, palette.solidFg)
    }
}

/** Failed-fetch state for the picker, with its own copy — "тарифы" would be the wrong noun here. */
@Composable
fun DestinationError(strings: SiteStrings, onRetry: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Column(
        Modifier.fillMaxWidth().padding(topBottom = 48.px).gap(18.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(strings.destError, Modifier.color(palette.ink2))
        SecondaryButton(strings.tariffsRetry, onRetry)
    }
}
